import { useEffect, useState } from 'react';

/**
 * [SEAMLESS-UPDATE 2026-08-13] KEEP PEOPLE'S PLACE ACROSS AN UPDATE RELOAD.
 *
 * A deploy can only reach an open tab by reloading the document — a browser tab runs the bundle it
 * downloaded, and nothing supported can swap that out underneath it. So "the app updates itself and
 * nobody notices" is not about avoiding the reload; it is about choosing a moment nobody is looking
 * AND putting them back exactly where they were. UpdateBanner owns the moment. This owns the place.
 *
 * Without this the reload is what gives the game away: every filter on this app is component state,
 * so a refresh drops you back to the default month with the search cleared and the list at the top.
 * That is the bit that reads as "something just happened", far more than the flicker does.
 *
 * ── WHY THE VALUE IS RESTORED AS `useState`'s INITIAL VALUE, not set afterwards in an effect ──
 * Restoring in an effect would mean the page mounts on its defaults, fires a fetch for the DEFAULT
 * month, then changes and fires a SECOND fetch for the real one — a visible flash of the wrong data
 * and double the API load, on every tab, on every deploy. Seeding the initial value means the first
 * render is already correct and only one request is ever made.
 *
 * ── FOUR RULES, each stopping a way this goes wrong ──
 *   ONE-SHOT     the payload is read once at module load and deleted immediately. A restore can
 *                never repeat, and a stale snapshot cannot haunt later navigation.
 *   OURS ONLY    only an update reload restores. A person who presses F5 themselves is asking for
 *                a fresh start and still gets one; this is not a general "remember my filters".
 *   PER USER     scoped to the signed-in user's id, so signing out and in as somebody else in the
 *                same tab cannot inherit the previous person's filters.
 *   SHORT LIVED  ignored beyond MAX_AGE_MS. A tab that was mid-reload when something went wrong
 *                should come back clean rather than restore a state from an hour ago.
 */

const KEY = 'dt_ui_snapshot';
/** Long enough to cover a slow reload, far short of "a state from earlier today". */
const MAX_AGE_MS = 2 * 60 * 1000;

interface Snapshot {
  /** Marks this as OUR reload. A manual refresh never writes one. */
  auto: true;
  userId: number | null;
  at: number;
  state: Record<string, unknown>;
  /** Scroll offset of the app's single scroll container — see AppLayout's data-page-scroll. */
  scrollTop?: number;
}

/** Live values, kept current by the hook below and read when a snapshot is taken. */
const live: Record<string, unknown> = {};

/* Declared BEFORE the reader below, which assigns them while it runs at module load. */
let pendingScrollTop: number | null = null;
let pendingUserId: number | null = null;

/**
 * Read ONCE at module load, then delete. Module scope is deliberate: this must happen before any
 * page component's first render, which is the whole point of seeding initial values.
 */
const restored: Record<string, unknown> | null = (() => {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY); // one-shot, whatever happens next
    if (!raw) return null;
    const snap = JSON.parse(raw) as Snapshot;
    if (!snap || snap.auto !== true) return null;
    if (typeof snap.at !== 'number' || Date.now() - snap.at > MAX_AGE_MS) return null;
    pendingScrollTop = typeof snap.scrollTop === 'number' ? snap.scrollTop : null;
    pendingUserId = snap.userId ?? null;
    return snap.state && typeof snap.state === 'object' ? snap.state : null;
  } catch {
    return null; // private mode, quota, corrupt payload — a clean start is always acceptable
  }
})();

/**
 * The per-user check cannot happen at module load — the signed-in user is not known until /auth/me
 * answers. So the payload is held and only handed out once the id matches, which the hook asks for
 * lazily on its first render. In practice `useState`'s initialiser runs long after auth resolves,
 * because the pages themselves are not mounted until then.
 */
let currentUserId: number | null = null;
export function setSnapshotUser(id: number | null): void {
  currentUserId = id;
}

function restoredValue(key: string): unknown {
  if (!restored) return undefined;
  // A snapshot with no recorded user (taken before sign-in resolved) is accepted; a MISMATCH is not.
  if (pendingUserId !== null && currentUserId !== null && pendingUserId !== currentUserId) return undefined;
  return restored[key];
}

/**
 * `useState`, except that after an update reload it comes back holding what it held before.
 *
 * `key` must be stable and unique across the app — prefix it with the page ('payments.months').
 * Values must survive JSON, which every filter here already does (strings, numbers, arrays, and
 * the {sortBy, sortOrder} objects).
 */
export function useRestorableState<T>(key: string, initial: T | (() => T)): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const r = restoredValue(key);
    if (r !== undefined) return r as T;
    return typeof initial === 'function' ? (initial as () => T)() : initial;
  });

  // Kept in an effect rather than during render so the registry is never written mid-render.
  useEffect(() => { live[key] = value; }, [key, value]);
  useEffect(() => () => { delete live[key]; }, [key]);

  return [value, setValue];
}

/**
 * Called by UpdateBanner immediately before an update reload — and ONLY there. Nothing else may
 * write this, or a manual refresh would start restoring too.
 */
export function captureUiSnapshot(userId: number | null): void {
  try {
    const scroller = document.querySelector('[data-page-scroll]') as HTMLElement | null;
    const snap: Snapshot = {
      auto: true,
      userId,
      at: Date.now(),
      state: { ...live },
      scrollTop: scroller ? scroller.scrollTop : undefined,
    };
    sessionStorage.setItem(KEY, JSON.stringify(snap));
  } catch {
    /* Storage unavailable or full. The reload still happens; it just lands on defaults, which is
       exactly today's behaviour — never let bookkeeping block the update itself. */
  }
}

/**
 * Puts the page card's scroll back where it was.
 *
 * ⚠ IT RETRIES, and that is the whole difficulty. Setting scrollTop before the rows exist does
 * nothing at all — silently — because the element has no scroll range yet, and every page here
 * loads its data asynchronously behind a skeleton. A single rAF would therefore "work" on a
 * warm cache and quietly do nothing on a cold one, which is the worst kind of half-feature. So it
 * checks a few times a second until the content is tall enough to hold the offset, then gives up.
 *
 * ⚠ SCOPE, and it is deliberately narrow: this restores the element carrying `data-page-scroll`,
 * which is AppLayout's page card. The dense table pages (Payments, Refunds, Disputes, New Sales,
 * Inventory) are in that layout's `$flush` list — the card does NOT scroll on those, each one
 * scrolls its own inner container instead. Those inner containers have no ref and differ per page,
 * so they are NOT restored and those pages come back at the top. Filters — which is what people
 * actually notice losing — are restored everywhere. Wiring the inner containers is a separate,
 * per-page job and should only be done if the top-of-list return is actually felt.
 */
export function restoreScrollTop(): void {
  if (pendingScrollTop === null) return;
  const top = pendingScrollTop;
  pendingScrollTop = null; // one-shot, like the rest of the payload
  if (top <= 0) return;

  const deadline = Date.now() + 3000;
  const attempt = () => {
    const el = document.querySelector('[data-page-scroll]') as HTMLElement | null;
    if (el && el.scrollHeight - el.clientHeight >= top) {
      el.scrollTop = top;
      return;
    }
    if (Date.now() < deadline) window.setTimeout(attempt, 120);
    // Past the deadline the data never arrived or the page is shorter than it was. Landing at the
    // top is a perfectly acceptable outcome; guessing a position is not.
  };
  window.setTimeout(attempt, 0);
}

/** True when this load is the far side of an update reload — used only for logging/diagnostics. */
export function wasRestored(): boolean {
  return restored !== null;
}
